import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import { Typography, FormControlLabel, Switch, Grid } from '@material-ui/core';
import { makeStyles, createStyles, withStyles, Theme } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';

import { apiChangeAvailability } from 'state/common.actions';
import { RootState } from 'state/store';
import { Colors, ConnectionState } from 'typings';

import DistributionTimer from './DistributionTimer';
import ConciergeMenu from './ConciergeMenu';
import InactiveDialog from './InactiveDialog';
import { ButtonPill } from '../shared/MuiExtensions';
import { Refresh } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import styled from 'styled-components';
import { AlertProps } from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(0, 1),
    },
    alert: {
      alignItems: 'center',
      borderRadius: theme.spacing(1),
      bottom: theme.spacing(-1.5),
      display: 'flex',
      flexDirection: 'row',
      left: theme.spacing(1.5),
      position: 'absolute',
      right: theme.spacing(1.5),
      top: 0,
      zIndex: theme.zIndex.appBar,
    },
    alertWithLead: {
      borderRadius: theme.spacing(1, 1, 0, 0),
    },
    subtitle1: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.RED,
    },
  })
);

const StyledGrid = styled(Grid)`
  align-items: stretch;
  margin-bottom: 0;
  margin-top: 0;

  .MuiGrid-item {
    display: flex;
    flex-direction: row;
    padding-bottom: 0;
    padding-top: 0;
    position: relative;
  }

  .MuiAlert-message {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }
`;

const AvailabilitySwitch = withStyles((theme: Theme) => ({
  switchBase: {
    '&$checked': {
      color: theme.palette.background.green,
    },
    '&$checked + $track': {
      backgroundColor: theme.palette.background.green,
    },
  },
  checked: {},
  track: {},
}))(Switch);

function TopBar() {
  const [inactiveDialogOpen, setInactiveDialogOpen] = React.useState(false);

  const onCancelInactiveReason = () => {
    setInactiveDialogOpen(false);
  };

  const onSubmitInactiveReason = (reason: string) => {
    dispatch(apiChangeAvailability({ isActive: false, reason }));
    setInactiveDialogOpen(false);
  };

  const dispatch = useDispatch();
  const { connectionUnavailable, updateAvailable } = useSelector(
    (state: RootState) => ({
      connectionUnavailable: state.app.connectionState === ConnectionState.UNAVAILABLE,
      updateAvailable: state.app.updateAvailable,
    }),
    shallowEqual
  );
  const concierge = useSelector(
    (state: RootState) => ({
      id: state.concierge.entity?.id || '',
      isActive: state.concierge.isActive,
      isAwaitingNewLead: state.concierge.isAwaitingNewLead,
      isOnCall: state.concierge.isOnCall,
      assignedLeadId: state.concierge.assignedLeadId,
      mayHaveMissedBroadcastEvents: state.concierge.mayHaveMissedBroadcastEvents,
    }),
    shallowEqual
  );
  const lead = useSelector(
    (state: RootState) => ({
      id: state.lead.entity?.id,
      conciergeId: state.lead.entity?.concierge?.id,
      conciergeName: state.lead.entity?.concierge?.name,
    }),
    shallowEqual
  );

  const distributedAt = useSelector((state: RootState) => state.lead.distribution?.createdAt);
  const classes = useStyles();

  const showBackToCurrentLead =
    concierge.assignedLeadId && lead.id && lead.id !== concierge.assignedLeadId;
  const showLeadAssignment = lead.id && concierge.id !== lead.conciergeId;
  const leadAssignment = lead.conciergeId
    ? `This lead is currently assigned to ${lead.conciergeName}`
    : 'This lead is currently unassigned';

  // convert distributedAt time in UTC to UNIX milliseconds
  let startTimerFrom = distributedAt ? dayjs.utc(distributedAt).valueOf() : null;

  const handleReload = () => {
    window.location.reload();
  };

  let alert: AlertProps | null = null;
  let alertMessage = '';

  // active concierges who don't have a lead assigned and may have missed broadcast events
  // need to be notified of new assignments they may have missed
  const mayHaveMissedNewAssignment =
    concierge.isActive &&
    (!concierge.assignedLeadId || concierge.isAwaitingNewLead) &&
    concierge.mayHaveMissedBroadcastEvents;

  if (connectionUnavailable || mayHaveMissedNewAssignment) {
    // real-time connectivity issues
    alert = {
      severity: 'error',
      action: (
        <ButtonPill
          size={'small'}
          type="button"
          startIcon={<Refresh />}
          disabled={concierge.isOnCall}
          onClick={handleReload}
        >
          Try Reloading
        </ButtonPill>
      ),
    };
    alertMessage = 'Lead connectivity issue';
  } else if (updateAvailable) {
    // new version of console is available
    alert = {
      severity: 'info',
      action: (
        <ButtonPill
          size={'small'}
          type="button"
          startIcon={<Refresh />}
          disabled={concierge.isOnCall}
          onClick={handleReload}
        >
          Update
        </ButtonPill>
      ),
    };
    alertMessage = 'Console update available';
  }

  const toggleSwitch = () => {
    !concierge.isActive
      ? dispatch(apiChangeAvailability({ isActive: !concierge.isActive }))
      : setInactiveDialogOpen(true);
  };

  return (
    <StyledGrid container alignItems="stretch" justifyContent="center" spacing={3}>
      <Grid item xs={12} sm={12} md={3} container alignItems={'center'} justifyContent={'flex-start'}>
        {showBackToCurrentLead && <Link to="/"> &lt;&lt; back to current lead</Link>}
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        container
        alignItems={alert ? 'stretch' : 'center'}
        justifyContent={'center'}
      >
        {!!alert && (
          <Alert
            className={classes.alert + (lead.id ? ' ' + classes.alertWithLead : '')}
            severity={alert.severity}
          >
            {alertMessage}
            {alert.action}
          </Alert>
        )}
        {showLeadAssignment && (
          <Typography variant="subtitle1" classes={{ subtitle1: classes.subtitle1 }}>
            <WarningIcon /> {leadAssignment}
          </Typography>
        )}
        {!showLeadAssignment && startTimerFrom && (
          <Typography variant="subtitle1" classes={{ subtitle1: classes.subtitle1 }}>
            <DistributionTimer startAt={startTimerFrom} />
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={12} md={3} container justifyContent={'flex-end'}>
        <FormControlLabel
          control={
            <AvailabilitySwitch
              checked={concierge.isActive}
              onChange={toggleSwitch}
              name="isActive"
              color="primary"
            />
          }
          label={
            <Typography variant="caption" color="textPrimary">
              {concierge.isActive ? 'active' : 'inactive'}
            </Typography>
          }
          labelPlacement="start"
          classes={{ root: classes.root }}
        />
        <InactiveDialog
          open={inactiveDialogOpen}
          onSubmit={onSubmitInactiveReason}
          onCancel={onCancelInactiveReason}
        />
        <ConciergeMenu />
      </Grid>
    </StyledGrid>
  );
}

export default TopBar;
